import { clone, map, merge, round, times } from "lodash";
import { mergeDeep } from "@apollo/client/utilities";
import mediaData from "../media/media.json";

export interface MediaItem {
  name: string;
  content: string;
}

export const getMediaContent = (name: string): string | undefined => {
  if (!mediaData) {
    return undefined;
  }
  const item = (mediaData as MediaItem[]).find(
    (m: MediaItem) => m.name === name
  );
  return item?.content;
};

export const overwriteComponent = (component: any, options: any) =>
  mergeDeep(clone(component), options);

export const COLORS = {
  BLUE_PRIMARY: "#0a2fb5",
  BLUE_LIGHT: "#869ff8",
  BLUE_TABLE: "#f4f6fc",
  BLUE_LIGHT_TABLE: "#F9F9FD",
  BLACK: "#000000",
  PANEL: "#F5F5F5",
  WHITE: "#FFFFFF",
  GRAY: "#acaaaa",
};

export const TEXT_OPTIONS = {
  caption: {
    fontSize: 12,
    color: COLORS.BLUE_PRIMARY,
    bold: true,
    align: "left",
    valign: "top",
    breakLine: true,
    paraSpaceAfter: 12,
  },
  caption2: {
    fontSize: 12,
    color: COLORS.BLACK,
    bold: true,
    align: "left",
    valign: "top",
    breakLine: true,
    paraSpaceAfter: 2,
  },
  body: {
    fontSize: 12,
    color: COLORS.BLACK,
    align: "left",
    valign: "top",
    breakLine: true,
    paraSpaceAfter: 12,
  },
  nameTitle: {
    fontSize: 36,
    color: COLORS.BLUE_PRIMARY,
    bold: true,
    align: "left",
    valign: "top",
    breakLine: true,
  },
  bullet: {
    fontSize: 12,
    color: COLORS.BLACK,
    align: "left",
    valign: "top",
    breakLine: true,
    paraSpaceAfter: 4,
    bullet: {
      code: "2022",
      indent: 12,
    },
  },
  columnTitle: {
    fontSize: 8,
    color: COLORS.BLACK,
    align: "center",
    valign: "center",
    fill: {
      color: COLORS.BLUE_TABLE,
    },
  },
} as const;

export const BACKGROUND_IMAGE_PATH = "ppt/media/image10.jpeg";
export const BACKGROUND_IMAGE_PATH_PSYCH = "ppt/media/rra-psych-background.png";
export const LOGO_IMAGE_PATH = "ppt/media/image5.png";
export const LOGO_IMAGE_PATH_FULL_NAME = "ppt/media/image6.png";
export const BRAND_WATERMARK_IMAGE_PATH = "ppt/media/image2.png";

export const OPACITY_OVERLAY = {
  rect: {
    x: 0,
    y: 0,
    h: "100%",
    w: "100%",
    fill: {
      color: "#FFFFFF",
      transparency: 30,
    },
  },
} as const;

export const BRAND_LOGO = {
  image: {
    data: getMediaContent(LOGO_IMAGE_PATH),
    x: 0.94,
    y: 0.9,
    w: 1.28,
    h: 1.64,
  },
} as const;

export const BRAND_LOGO_FULL_NAME = {
  image: {
    data: getMediaContent(LOGO_IMAGE_PATH_FULL_NAME),
    x: 0.86,
    y: 0.89,
    w: 1.9,
    h: 0.74,
  },
} as const;

export const PAGE_TITLE_TEXT = {
  placeholder: {
    options: {
      name: "title",
      type: "title",
      x: 0.52,
      y: 0.52,
      w: 8.15,
      h: 0.62,
      fontSize: 40,
      align: "left",
    },
  },
  text: "Page Title",
} as const;

export const PRIVATE_AND_CONFIDENTIAL_TEXT = {
  text: {
    text: "Private & Confidential",
    options: {
      x: 0.52,
      y: "95%",
      fontSize: 8,
      color: COLORS.BLACK,
    },
  },
} as const;

export const BRAND_WATERMARK = {
  image: {
    data: getMediaContent(BRAND_WATERMARK_IMAGE_PATH),
    x: 14.56,
    y: 8.3,
    w: 0.93,
    h: 0.35,
  },
};

export const SPLIT_PANEL_LEFT = {
  rect: {
    x: 0,
    y: 0,
    w: "48%",
    h: "100%",
    fill: {
      color: COLORS.PANEL,
    },
  },
} as const;

export const SPLIT_PANEL_RIGHT = mergeDeep(clone(SPLIT_PANEL_LEFT), {
  rect: {
    x: "55%",
    w: "45%",
  },
} as const);

export const SPLIT_PANEL_BOTTOM = mergeDeep(clone(SPLIT_PANEL_LEFT), {
  rect: {
    y: 2.05,
    w: "100%",
    h: 6.95,
    line: {
      width: 1,
      color: COLORS.BLUE_PRIMARY,
    },
  },
} as const);

export const SPLIT_PANEL_BOTTOM_SMALL = mergeDeep(clone(SPLIT_PANEL_LEFT), {
  rect: {
    y: 6.62,
    w: "100%",
    h: 2.38,
    fill: {
      color: COLORS.PANEL,
    },
  },
} as const);

interface ScoreTableData {
  groupTitle: string;
  rows: {
    title: string;
    score: number;
  }[];
}

export const getScoreTableRows = (data: ScoreTableData) => [
  [
    {
      text: data.groupTitle,
      options: {
        ...TEXT_OPTIONS.caption,
        fontSize: 12,
        align: "left",
        valign: "middle",
        fill: COLORS.BLUE_TABLE,
        colW: 2,
      },
    },
    {
      text: "EMERGING",
      options: {
        ...TEXT_OPTIONS.columnTitle,
        align: "center",
        valign: "middle",
        fill: COLORS.BLUE_TABLE,
      },
    },
    {
      text: "MODERATE",
      options: {
        ...TEXT_OPTIONS.columnTitle,
        align: "center",
        valign: "middle",
        fill: COLORS.BLUE_TABLE,
      },
    },
    {
      text: "HIGH",
      options: {
        ...TEXT_OPTIONS.columnTitle,
        align: "center",
        valign: "middle",
        fill: COLORS.BLUE_TABLE,
      },
    },
    {
      text: "DISTINCTIVE",
      options: {
        ...TEXT_OPTIONS.columnTitle,
        align: "center",
        valign: "middle",
        fill: COLORS.BLUE_TABLE,
      },
    },
  ],
  ...map(data.rows, (row) => [
    {
      text: row.title,
      options: {
        ...TEXT_OPTIONS.body,
        fontSize: 12,
        align: "left",
        valign: "middle",
        colW: 2,
      },
    },
    ...times(4, (index) => ({
      text: "●",
      options: {
        shape: "ellipse",
        align: "center",
        valign: "middle",
        fontSize: 20,
        color:
          round(row.score) === index + 1
            ? COLORS.BLUE_PRIMARY
            : COLORS.BLUE_TABLE,
      },
    })),
  ]),
];
